import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/components/ui/toaster'
import { saveConfig, testEmail, testWhatsApp, type ConfigPayload } from '@/lib/api'
import { AlertTriangle, Mail, MessageCircle, Ticket, FileText, ArrowRight, Send, Zap } from 'lucide-react'

const PRESETS = {
  founder: `You are an ambient operations guardian. You will receive a stream of operational events.
Act rarely. Ignore normal/healthy noise.
Alert only when:
- A payment failure is >= $100 OR a VIP customer is affected
- The same production error repeats 3+ times within 5 minutes
- A security anomaly is high confidence (new country + new device)
Otherwise, add notable items to a digest but do not alert immediately.
Use WhatsApp for urgent issues and Email for non-urgent summaries.
Apply a 15 minute cooldown per incident to avoid duplicate alerts.
When uncertain, prefer IGNORE or DIGEST instead of alerting.`,
  payments: `You are a payments guardian. Focus on financial events only.
Alert when:
- Any payment failure >= $200
- Any VIP customer payment issue (regardless of amount)
- Repeated payment failures from same customer (2+ in 1 hour)
Ignore all non-payment events.
Use WhatsApp for all alerts (payments are always urgent).`,
  reliability: `You are a reliability/on-call guardian.
Alert when:
- Error rate spikes (3+ similar errors in 5 minutes)
- Service degradation (latency > threshold, availability drops)
- Deployment failures or rollbacks
Ignore routine metrics and successful deployments.
Create tickets for tracking, use WhatsApp for urgent alerts.`,
}

export default function Config() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [testingEmail, setTestingEmail] = useState(false)
  const [testingWhatsApp, setTestingWhatsApp] = useState(false)

  const [config, setConfig] = useState<ConfigPayload>({
    policy_text: PRESETS.founder,
    cooldown_minutes: 15,
    repetition_threshold: 3,
    repetition_window_minutes: 5,
    max_alerts_per_hour: 4,
    urgency_threshold: 'medium',
    tools_enabled: {
      email: true,
      whatsapp: true,
      ticket: true,
      digest: true,
    },
    tool_params: {
      to_email: '',
      to_phone: '',
      ticket_project: 'OPS',
    },
    model: 'agentic-turbo',
    debug: false,
    dry_run: false,
  })

  const handleSave = async (andNavigate = false) => {
    setLoading(true)
    try {
      await saveConfig(config)
      toast({ title: 'Configuration saved', description: 'You can now inject events in the Stream.' })
      if (andNavigate) {
        navigate('/demo/stream')
      }
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleTestEmail = async () => {
    if (!config.tool_params.to_email) {
      toast({ title: 'Error', description: 'Please enter an email address first', variant: 'destructive' })
      return
    }
    setTestingEmail(true)
    try {
      const result = await testEmail(config.tool_params.to_email)
      toast({ title: result.ok ? 'Success' : 'Failed', description: result.message })
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' })
    } finally {
      setTestingEmail(false)
    }
  }

  const handleTestWhatsApp = async () => {
    if (!config.tool_params.to_phone) {
      toast({ title: 'Error', description: 'Please enter a phone number first', variant: 'destructive' })
      return
    }
    setTestingWhatsApp(true)
    try {
      const result = await testWhatsApp(config.tool_params.to_phone)
      toast({ title: result.ok ? 'Success' : 'Failed', description: result.message })
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' })
    } finally {
      setTestingWhatsApp(false)
    }
  }

  return (
    <div className="container py-8">
      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Left sidebar - How to run */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How to run this demo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="font-medium">Step 1 — Define behavior</p>
                <p className="text-muted-foreground">Write what you want the agent to pay attention to.</p>
                <p className="text-xs text-accent mt-1">Tip: Short + constrained works best</p>
              </div>
              <div>
                <p className="font-medium">Step 2 — Enable tools</p>
                <p className="text-muted-foreground">Turn on Email/WhatsApp and provide destinations.</p>
                <p className="text-xs text-muted-foreground mt-1">Keys are stored server-side only.</p>
              </div>
              <div>
                <p className="font-medium">Step 3 — Send a test</p>
                <p className="text-muted-foreground">Use test buttons to verify delivery.</p>
              </div>
              <div>
                <p className="font-medium">Step 4 — Inject events</p>
                <p className="text-muted-foreground">Go to Stream and click event buttons.</p>
                <p className="text-xs text-muted-foreground mt-1">Expect most to be ignored.</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-md text-yellow-800 text-xs">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>When enabled, the demo may send real email/WhatsApp messages.</span>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Main content */}
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-3xl">Ops Feed Guardian</h1>
            <p className="text-muted-foreground">An ambient agent that watches noisy operational events and only acts when it matters.</p>
          </div>

          {/* Quick Demo Card */}
          <Card className="border-accent/50 bg-accent/5">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="h-5 w-5 text-accent" />
                Quick Demo (2 minutes)
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li><strong className="text-foreground">Leave defaults</strong> — "Founder Guardrails" policy is already configured</li>
                <li><strong className="text-foreground">Enter your email</strong> — Scroll to Email Tool section below</li>
                <li><strong className="text-foreground">Click "Test"</strong> — Verify you receive the test message</li>
                <li><strong className="text-foreground">Click "Save & Go to Stream"</strong> — Navigate to event injection</li>
                <li><strong className="text-foreground">Click "Auto-run: Incident"</strong> — Watch agent handle 12 events</li>
                <li><strong className="text-foreground">Observe decisions</strong> — Most ignored, 1-2 real alerts sent</li>
              </ol>
            </CardContent>
          </Card>

          {/* Policy Editor */}
          <Card>
            <CardHeader>
              <CardTitle>Behavior Policy</CardTitle>
              <CardDescription>Describe the behavior in natural language</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge
                  variant={config.policy_text === PRESETS.founder ? 'act' : 'secondary'}
                  className="cursor-pointer"
                  onClick={() => setConfig({ ...config, policy_text: PRESETS.founder })}
                >
                  Founder Guardrails
                </Badge>
                <Badge
                  variant={config.policy_text === PRESETS.payments ? 'act' : 'secondary'}
                  className="cursor-pointer"
                  onClick={() => setConfig({ ...config, policy_text: PRESETS.payments })}
                >
                  Payments-First
                </Badge>
                <Badge
                  variant={config.policy_text === PRESETS.reliability ? 'act' : 'secondary'}
                  className="cursor-pointer"
                  onClick={() => setConfig({ ...config, policy_text: PRESETS.reliability })}
                >
                  Reliability / On-call
                </Badge>
              </div>
              <Textarea
                value={config.policy_text}
                onChange={(e) => setConfig({ ...config, policy_text: e.target.value })}
                rows={10}
                className="font-mono text-sm"
              />
            </CardContent>
          </Card>

          {/* Guardrail Knobs */}
          <Card>
            <CardHeader>
              <CardTitle>Guardrails</CardTitle>
              <CardDescription>Configure cooldown, repetition, and budget limits</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Cooldown (minutes)</Label>
                  <Input
                    type="number"
                    value={config.cooldown_minutes}
                    onChange={(e) => setConfig({ ...config, cooldown_minutes: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Repetition threshold</Label>
                  <Input
                    type="number"
                    value={config.repetition_threshold}
                    onChange={(e) => setConfig({ ...config, repetition_threshold: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Repetition window (minutes)</Label>
                  <Input
                    type="number"
                    value={config.repetition_window_minutes}
                    onChange={(e) => setConfig({ ...config, repetition_window_minutes: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max alerts per hour</Label>
                  <Input
                    type="number"
                    value={config.max_alerts_per_hour}
                    onChange={(e) => setConfig({ ...config, max_alerts_per_hour: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Urgency threshold</Label>
                  <Select
                    value={config.urgency_threshold}
                    onValueChange={(v) => setConfig({ ...config, urgency_threshold: v as 'low' | 'medium' | 'high' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Dry run mode</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config.dry_run}
                      onCheckedChange={(v) => setConfig({ ...config, dry_run: v })}
                    />
                    <span className="text-sm text-muted-foreground">
                      {config.dry_run ? 'Actions not sent' : 'Real sending'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tools Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Tools</CardTitle>
              <CardDescription>Enable and configure action tools</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Email */}
              <div className="flex items-start gap-4 p-4 border rounded-lg">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Email</Label>
                    <Switch
                      checked={config.tools_enabled.email}
                      onCheckedChange={(v) => setConfig({
                        ...config,
                        tools_enabled: { ...config.tools_enabled, email: v }
                      })}
                    />
                  </div>
                  {config.tools_enabled.email && (
                    <>
                      <div className="flex gap-2">
                        <Input
                          placeholder="recipient@example.com"
                          value={config.tool_params.to_email || ''}
                          onChange={(e) => setConfig({
                            ...config,
                            tool_params: { ...config.tool_params, to_email: e.target.value }
                          })}
                        />
                        <Button variant="outline" size="sm" onClick={handleTestEmail} disabled={testingEmail}>
                          <Send className="h-4 w-4 mr-1" />
                          {testingEmail ? 'Sending...' : 'Test'}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Enter your email address to receive alerts. Powered by Resend API (server-side).
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* WhatsApp */}
              <div className="flex items-start gap-4 p-4 border rounded-lg">
                <MessageCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">WhatsApp</Label>
                    <Switch
                      checked={config.tools_enabled.whatsapp}
                      onCheckedChange={(v) => setConfig({
                        ...config,
                        tools_enabled: { ...config.tools_enabled, whatsapp: v }
                      })}
                    />
                  </div>
                  {config.tools_enabled.whatsapp && (
                    <>
                      <div className="flex gap-2">
                        <Input
                          placeholder="+14155552671"
                          value={config.tool_params.to_phone || ''}
                          onChange={(e) => setConfig({
                            ...config,
                            tool_params: { ...config.tool_params, to_phone: e.target.value }
                          })}
                        />
                        <Button variant="outline" size="sm" onClick={handleTestWhatsApp} disabled={testingWhatsApp}>
                          <Send className="h-4 w-4 mr-1" />
                          {testingWhatsApp ? 'Sending...' : 'Test'}
                        </Button>
                      </div>
                      <div className="text-xs space-y-2">
                        <p className="text-muted-foreground"><strong>Format:</strong> E.164 format required (e.g., +14155552671)</p>
                        <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
                          <p className="font-medium">Important: Opt-in required for sandbox</p>
                          <p className="mt-1">To receive WhatsApp messages, you must first send any message to <strong>+91 6300 575269</strong> from your WhatsApp. This is required by Meta to prevent spam.</p>
                        </div>
                        <p className="text-muted-foreground"><strong>Setup:</strong> Requires Meta WhatsApp Cloud API. <a href="/docs/guides/dry-run-rate-limits" className="text-accent hover:underline">See docs</a> for configuration.</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Ticket */}
              <div className="flex items-start gap-4 p-4 border rounded-lg">
                <Ticket className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Create Ticket (Mock)</Label>
                    <Switch
                      checked={config.tools_enabled.ticket}
                      onCheckedChange={(v) => setConfig({
                        ...config,
                        tools_enabled: { ...config.tools_enabled, ticket: v }
                      })}
                    />
                  </div>
                  {config.tools_enabled.ticket && (
                    <Input
                      placeholder="Project name"
                      value={config.tool_params.ticket_project || ''}
                      onChange={(e) => setConfig({
                        ...config,
                        tool_params: { ...config.tool_params, ticket_project: e.target.value }
                      })}
                    />
                  )}
                </div>
              </div>

              {/* Digest */}
              <div className="flex items-start gap-4 p-4 border rounded-lg">
                <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Digest (In-app)</Label>
                    <Switch
                      checked={config.tools_enabled.digest}
                      onCheckedChange={(v) => setConfig({
                        ...config,
                        tools_enabled: { ...config.tools_enabled, digest: v }
                      })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Collects notable items and shows them in the digest panel.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Model Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Model</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select
                    value={config.model}
                    onValueChange={(v) => setConfig({ ...config, model: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agentic-turbo">agentic-turbo (fast)</SelectItem>
                      <SelectItem value="agentic-thinker">agentic-thinker (more reasoning)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Debug mode</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config.debug}
                      onCheckedChange={(v) => setConfig({ ...config, debug: v })}
                    />
                    <span className="text-sm text-muted-foreground">
                      Show decision trace + reasoning
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => handleSave(false)} disabled={loading}>
              Save Configuration
            </Button>
            <Button onClick={() => handleSave(true)} disabled={loading}>
              Save & Go to Stream
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
